import React, { useEffect, useState } from "react";
import { Download, ExternalLink, ShieldCheck, Plus, X, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { k: "",              l: "All" },
  { k: "ai",            l: "AI Tools" },
  { k: "dev",           l: "Developer" },
  { k: "browser",       l: "Browsers" },
  { k: "office",        l: "Office" },
  { k: "design",        l: "Design" },
  { k: "media",         l: "Media" },
  { k: "security",      l: "Security" },
  { k: "utility",       l: "Utility" },
  { k: "learning",      l: "Learning" },
  { k: "communication", l: "Communication" },
  { k: "productivity",  l: "Productivity" },
  { k: "game",          l: "Games" },
];

const PLATFORM_LABEL = { windows: "Win", mac: "macOS", linux: "Linux", ios: "iOS", android: "Android", web: "Web", cross: "Cross" };

// Curated seed shown when the directory is empty — jump-starts the page with
// unambiguously "genuine" software links. These are just anchor tags; nothing
// is downloaded through find2near.
const SEED = [
  { name: "ChatGPT",  publisher: "OpenAI",     category: "ai",       platforms: ["web","ios","android","mac","windows"], url: "https://chat.openai.com",           icon_url: null, is_free: true, description: "OpenAI's flagship chat assistant." },
  { name: "Google Gemini", publisher: "Google", category: "ai",      platforms: ["web","android","ios"],                url: "https://gemini.google.com",         icon_url: null, is_free: true, description: "Google's multimodal AI." },
  { name: "Claude",   publisher: "Anthropic",  category: "ai",       platforms: ["web","ios","android","mac"],           url: "https://claude.ai",                  icon_url: null, is_free: true, description: "Anthropic's helpful, harmless AI." },
  { name: "Visual Studio Code", publisher: "Microsoft", category: "dev", platforms: ["windows","mac","linux"],           url: "https://code.visualstudio.com",      icon_url: null, is_free: true, description: "The most-loved open-source code editor." },
  { name: "Firefox",  publisher: "Mozilla",    category: "browser",  platforms: ["windows","mac","linux","ios","android"], url: "https://www.mozilla.org/firefox", icon_url: null, is_free: true, description: "Privacy-first browser." },
  { name: "Brave",    publisher: "Brave Software", category: "browser", platforms: ["windows","mac","linux","ios","android"], url: "https://brave.com",           icon_url: null, is_free: true, description: "Ad-blocking, privacy-first browser." },
  { name: "LibreOffice", publisher: "The Document Foundation", category: "office", platforms: ["windows","mac","linux"], url: "https://www.libreoffice.org",       icon_url: null, is_free: true, description: "Free, open-source Microsoft Office alternative." },
  { name: "GIMP",     publisher: "GNOME",      category: "design",   platforms: ["windows","mac","linux"],               url: "https://www.gimp.org",              icon_url: null, is_free: true, description: "Free Photoshop-class image editor." },
  { name: "Blender",  publisher: "Blender Foundation", category: "design", platforms: ["windows","mac","linux"],        url: "https://www.blender.org",           icon_url: null, is_free: true, description: "Open-source 3D creation suite." },
  { name: "VLC Media Player", publisher: "VideoLAN", category: "media", platforms: ["windows","mac","linux","ios","android"], url: "https://www.videolan.org/vlc/", icon_url: null, is_free: true, description: "Plays literally every media file." },
  { name: "Bitwarden", publisher: "Bitwarden", category: "security", platforms: ["web","windows","mac","linux","ios","android"], url: "https://bitwarden.com",     icon_url: null, is_free: true, description: "Open-source password manager." },
  { name: "Signal",   publisher: "Signal Foundation", category: "communication", platforms: ["ios","android","windows","mac","linux"], url: "https://signal.org", icon_url: null, is_free: true, description: "End-to-end encrypted messaging." },
];

export default function Software() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [platform, setPlatform] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  useSeo({
    title: "Genuine software downloads & AI tools · find2near",
    description: "Curated genuine download links for AI apps, developer tools, browsers, security suites and more. Community-published, official-links only.",
    canonical: "/software",
  });

  const load = () => {
    setLoading(true);
    const params = { limit: 100 };
    if (cat)      params.category = cat;
    if (platform) params.platform = platform;
    if (q)        params.q = q;
    api.get("/software", { params })
       .then((r) => setItems(r.data.length > 0 ? r.data : SEED))
       .catch(() => setItems(SEED))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [cat, platform]); // eslint-disable-line

  const track = (id, url) => {
    if (id) api.post(`/software/${id}/click`).catch(() => {});
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="software-page">
      <SoftForm open={showForm} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Sparkles size={12} className="text-sky-600" /> Genuine downloads · AI · Tools</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Trusted software. <span className="text-sky-600">Zero malware.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Only official links to first-party sources. We never host binaries — you download from the vendor directly.</p>
        </div>
        {user && (
          <Button onClick={() => setShowForm(true)} className="rounded-full bg-sky-600 hover:bg-sky-700 font-black" data-testid="soft-new-btn">
            <Plus size={14} className="mr-1.5" /> Suggest software
          </Button>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search apps…" className="rounded-full h-9 flex-1 min-w-[180px] text-sm" data-testid="soft-q" />
        <Select value={cat || "__all__"} onValueChange={(v) => setCat(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-44 text-xs" data-testid="soft-cat"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.k || "__all__"} value={c.k || "__all__"}>{c.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={platform || "__all__"} onValueChange={(v) => setPlatform(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-36 text-xs" data-testid="soft-platform"><SelectValue placeholder="Any platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any platform</SelectItem>
            {Object.entries(PLATFORM_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => (
            <div key={s.soft_id || s.name} className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`soft-card-${s.soft_id || s.name}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-sky-700 font-black">{s.category}</div>
                  <div className="font-black text-stone-900">{s.name}</div>
                  {s.publisher && <div className="text-[11px] text-stone-500">{s.publisher}</div>}
                </div>
                {s.verified && <span className="text-[10px] font-black text-emerald-700 inline-flex items-center gap-0.5"><ShieldCheck size={10} /> verified</span>}
              </div>
              {s.description && <p className="text-xs text-stone-600 mt-2 line-clamp-3">{s.description}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {(s.platforms || []).slice(0, 4).map((p) => <span key={p} className="text-[10px] font-black bg-stone-100 rounded-full px-2 py-0.5 uppercase">{PLATFORM_LABEL[p] || p}</span>)}
                {s.is_free && <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">FREE</span>}
              </div>
              <button onClick={() => track(s.soft_id, s.url)}
                      className="mt-3 w-full h-9 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-black inline-flex items-center justify-center gap-1.5"
                      data-testid={`soft-link-${s.soft_id || s.name}`}>
                <Download size={13} /> Get from official site <ExternalLink size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyS = () => ({ name: "", publisher: "", category: "utility", platforms: [], url: "", icon_url: "", is_free: true, description: "" });

function SoftForm({ open, onClose, onSaved }) {
  const [f, setF] = useState(emptyS());
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const togglePlatform = (p) => set({ platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p] });

  const submit = async () => {
    if (f.name.trim().length < 2 || !f.url.startsWith("http")) { toast.error("Name + official https URL required"); return; }
    setBusy(true);
    try {
      await api.post("/software", f);
      toast.success("Suggestion added");
      setF(emptyS());
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-sky-50 to-white">
          <b>Suggest genuine software</b>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="App name" className="rounded-2xl h-10" />
          <Input value={f.publisher} onChange={(e) => set({ publisher: e.target.value })} placeholder="Publisher (e.g. Google, Microsoft)" className="rounded-2xl h-10" />
          <Input value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="Official https://… URL" className="rounded-2xl h-10 font-mono" />
          <Select value={f.category} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger className="rounded-2xl h-10"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.filter(c => c.k).map(c => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Platforms</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(PLATFORM_LABEL).map(([k, v]) => (
                <button key={k} onClick={() => togglePlatform(k)} type="button" className={`text-[11px] font-black rounded-full px-3 py-1 border ${f.platforms.includes(k) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-stone-700 border-stone-200"}`}>{v}</button>
              ))}
            </div>
          </div>
          <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="What does it do?" className="w-full px-4 py-3 rounded-2xl border border-stone-200 outline-none text-sm" />
          <label className="text-sm text-stone-700 inline-flex items-center gap-2"><input type="checkbox" checked={f.is_free} onChange={(e) => set({ is_free: e.target.checked })} /> Free</label>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-sky-600 hover:bg-sky-700 font-black">{busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null} Publish</Button>
        </div>
      </div>
    </div>
  );
}
